import apiClient from './apiClient';

// Dashboard
export const dashboardApi = {
  get: () => apiClient.get('/dashboard'),
};

// Excel Export
export const exportApi = {
  invoicesXlsx: () => apiClient.get('/export/invoices/xlsx', { responseType: 'blob' }),
  purchaseOrdersXlsx: () => apiClient.get('/export/purchase-orders/xlsx', { responseType: 'blob' }),
  shipmentsXlsx: () => apiClient.get('/export/shipments/xlsx', { responseType: 'blob' }),
  productsXlsx: () => apiClient.get('/export/products/xlsx', { responseType: 'blob' }),
  inventoryXlsx: () => apiClient.get('/export/inventory/xlsx', { responseType: 'blob' }),
};

// Purchase Orders
export const purchaseOrdersApi = {
  list: (params: any) => apiClient.get('/purchaseorders', { params }),
  get: (id: string) => apiClient.get(`/purchaseorders/${id}`),
  confirm: (id: string, data: any) => apiClient.post(`/purchaseorders/${id}/confirm`, data),
  sync: () => apiClient.post('/purchaseorders/sync'),
  bulkConfirm: (ids: string[]) => apiClient.post('/purchaseorders/bulk-confirm', { ids }),
  exportCsv: (params: any) => apiClient.get('/purchaseorders/export', { params, responseType: 'blob' }),
};

// Shipments
export const shipmentsApi = {
  list: (params: any) => apiClient.get('/shipments', { params }),
  get: (id: string) => apiClient.get(`/shipments/${id}`),
  create: (data: any) => apiClient.post('/shipments', data),
  submit: (id: string) => apiClient.post(`/shipments/${id}/submit`),
  getLabel: (id: string) => apiClient.get(`/shipments/${id}/label`, { responseType: 'blob' }),
  requestLabel: (id: string) => apiClient.post(`/shipments/${id}/request-label`),
  uploadPackingDoc: (id: string, formData: FormData) =>
    apiClient.post(`/shipments/${id}/packing-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getPackingDoc: (docId: string) =>
    apiClient.get(`/shipments/packing-documents/${docId}`, { responseType: 'blob' }),
  bulkSubmit: (ids: string[]) => apiClient.post('/shipments/bulk-submit', { ids }),
  exportCsv: (params: any) => apiClient.get('/shipments/export', { params, responseType: 'blob' }),
};

// Invoices
export const invoicesApi = {
  list: (params: any) => apiClient.get('/invoices', { params }),
  get: (id: string) => apiClient.get(`/invoices/${id}`),
  create: (data: any) => apiClient.post('/invoices', data),
  update: (id: string, data: any) => apiClient.put(`/invoices/${id}`, data),
  delete: (id: string) => apiClient.delete(`/invoices/${id}`),
  submit: (id: string) => apiClient.post(`/invoices/${id}/submit`),
  bulkSubmit: (invoiceIds: string[]) => apiClient.post('/invoices/bulk-submit', { invoiceIds }),
  getPdf: (id: string) => apiClient.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  bulkDownload: (invoiceIds: string[]) =>
    apiClient.post('/invoices/bulk-download', { invoiceIds }, { responseType: 'blob' }),
  exportCsv: (params: any) =>
    apiClient.get('/invoices/export', { params, responseType: 'blob' }),
  importCsv: (formData: FormData) =>
    apiClient.post('/invoices/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  generateFromPo: (purchaseOrderId: string) =>
    apiClient.post(`/invoices/generate-from-po/${purchaseOrderId}`),
  createCreditNote: (invoiceId: string, data: any) =>
    apiClient.post(`/invoices/${invoiceId}/credit-note`, data),
  generatePdf: (id: string) => apiClient.post(`/invoices/${id}/generate-pdf`, {}, { responseType: 'blob' }),
  getAuditLog: (id: string) => apiClient.get(`/invoices/${id}/audit-log`),
  resubmit: (id: string) => apiClient.post(`/invoices/${id}/resubmit`),
  batchGeneratePdfs: (invoiceIds: string[]) =>
    apiClient.post('/invoices/batch-generate-pdfs', { invoiceIds }, { responseType: 'blob' }),
  syncPdfs: () => apiClient.post('/invoices/sync-pdfs'),
  getCompanyProfile: () => apiClient.get('/invoices/company-profile'),
  saveCompanyProfile: (data: any) => apiClient.put('/invoices/company-profile', data),
};

// Products
export const productsApi = {
  list: (params: any) => apiClient.get('/products', { params }),
  get: (id: string) => apiClient.get(`/products/${id}`),
  update: (id: string, data: any) => apiClient.put(`/products/${id}`, data),
  syncPricing: (id: string) => apiClient.post(`/products/${id}/sync-pricing`),
  sync: () => apiClient.post('/products/sync'),
  bulkSyncPricing: (ids: string[]) => apiClient.post('/products/bulk-sync-pricing', { ids }),
  bulkUpdateStatus: (ids: string[], status: number) => apiClient.post('/products/bulk-update-status', { ids, status }),
  getAuditLog: (id: string) => apiClient.get(`/products/${id}/audit-log`),
};

// Inventory
export const inventoryApi = {
  list: (params: any) => apiClient.get('/inventory', { params }),
  update: (data: any) => apiClient.put('/inventory', data),
  syncFromAmazon: () => apiClient.post('/inventory/sync-from-amazon'),
  syncToAmazon: () => apiClient.post('/inventory/sync-to-amazon'),
};

// Users
export const usersApi = {
  list: () => apiClient.get('/users'),
  updateRole: (id: string, data: any) => apiClient.put(`/users/${id}/role`, data),
  me: () => apiClient.get('/users/me'),
};

// Files
export const filesApi = {
  upload: (formData: FormData) =>
    apiClient.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  get: (id: string) => apiClient.get(`/files/${id}`, { responseType: 'blob' }),
  listForEntity: (entityType: string, entityId: string) =>
    apiClient.get(`/files/entity/${entityType}/${entityId}`),
  delete: (id: string) => apiClient.delete(`/files/${id}`),
};

// Sync
export const syncApi = {
  syncAll: () => apiClient.post('/sync/all'),
  logs: (params: any) => apiClient.get('/sync/logs', { params }),
};

// Permissions
export const permissionsApi = {
  me: () => apiClient.get('/permissions/me'),
  modules: () => apiClient.get('/permissions/modules'),
  getForUser: (userId: string) => apiClient.get(`/permissions/user/${userId}`),
  setForUser: (userId: string, permissions: Record<string, string>) =>
    apiClient.put(`/permissions/user/${userId}`, permissions),
};
